import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { cn } from "@strivelabs/ui-core";
import {
  ComposerContext,
  type ComposerContextValue,
  type ComposerMenu,
} from "./context";
import type { ComposerModeOption } from "./types";

export interface ComposerProps {
  /** Controlled text value. Omit for uncontrolled use with `defaultValue`. */
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Called with the text on submit (Enter or send). */
  onSubmit?: (text: string) => void;
  /** Response-mode catalog. */
  modes?: ComposerModeOption[];
  /** Active mode id (controlled). */
  mode?: string;
  onModeChange?: (id: string) => void;
  disabled?: boolean;
  /** True while streaming/submitting — the send control becomes Stop. */
  busy?: boolean;
  /** Allow submit with empty text (for example, an attachment-only send). */
  allowEmptySubmit?: boolean;
  /** Block submit regardless of text (for example, while an upload is in flight). */
  submitBlocked?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Composer root: a form that provides shared composer state to its parts.
 * Presentational and prop-driven — data (mode, submit, attachments) is wired
 * by the consumer.
 */
export const Composer = ({
  value: controlledValue,
  defaultValue = "",
  onValueChange,
  onSubmit,
  modes = [],
  mode,
  onModeChange,
  disabled = false,
  busy = false,
  allowEmptySubmit = false,
  submitBlocked = false,
  className,
  children,
}: ComposerProps) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [openMenu, setOpenMenu] = useState<ComposerMenu>(null);

  // ⌘/Ctrl+U toggles the attach menu, ⌘/Ctrl+M toggles the mode popover.
  useEffect(() => {
    if (disabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === "u") {
        event.preventDefault();
        setOpenMenu((prev) => (prev === "attach" ? null : "attach"));
      } else if (key === "m") {
        event.preventDefault();
        setOpenMenu((prev) => (prev === "mode" ? null : "mode"));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [disabled]);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const setValue = (next: string) => {
    if (!isControlled) setInternalValue(next);
    onValueChange?.(next);
  };

  const hasText = value.trim().length > 0;
  const canSubmit = (hasText || allowEmptySubmit) && !submitBlocked;

  const submit = () => {
    if (disabled || busy || !canSubmit) return;
    onSubmit?.(value);
    setValue("");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const context: ComposerContextValue = {
    value,
    setValue,
    hasText,
    canSubmit,
    disabled,
    busy,
    modes,
    mode,
    setMode: (id) => onModeChange?.(id),
    openMenu,
    setOpenMenu,
    submit,
  };

  return (
    <ComposerContext.Provider value={context}>
      <form onSubmit={handleSubmit} className={cn("relative", className)}>
        {children}
      </form>
    </ComposerContext.Provider>
  );
};
